import { evaluatePlatformReadiness } from '../../../../shared/posting-readiness';

import { snapshotFromPost } from '@/lib/approvals/snapshot';
import { candidatePostTitle } from '@/lib/posts/post-title';
import type { ApprovalCandidate } from '@/services/data/ApprovalService';

export interface ApprovalPostSource {
  id: string;
  content?: string | null;
  title?: string | null;
  platforms?: string[] | null;
  media_urls?: string[] | null;
  media_type?: string | null;
  scheduled_at?: string | null;
  created_at?: string | null;
}

/**
 * One post as an approval candidate, built the same way
 * `ApprovalService.listDraftCandidates` builds its rows (same `key`, same
 * snapshot) — so a post sent from the inspector and the same post ticked in the
 * dialog are the identical item. Null when there is no copy to judge.
 */
export function candidateFromPost(post: ApprovalPostSource): ApprovalCandidate | null {
  const content = (post.content ?? '').trim();
  if (!content) return null;
  return {
    key: `post:${post.id}`,
    itemKind: 'post',
    title: candidatePostTitle(post),
    body: null,
    occursOn: post.scheduled_at ? post.scheduled_at.slice(0, 10) : null,
    occursLabel: null,
    socialPostId: post.id,
    projectTaskId: null,
    snapshot: snapshotFromPost(post),
    hasDraft: true,
  };
}

/** Candidate list with the preselected post included once, and only it ticked. */
export function mergePreselectedCandidate(
  candidates: ApprovalCandidate[],
  preselected: ApprovalCandidate | null | undefined,
): ApprovalCandidate[] {
  if (!preselected) return candidates;
  return candidates.some((c) => c.key === preselected.key)
    ? candidates
    : [preselected, ...candidates];
}

export function isCandidateReadyToPublish(candidate: ApprovalCandidate): boolean {
  const { platform, body, mediaUrls, mediaType } = candidate.snapshot;
  if (!platform) return false;
  return evaluatePlatformReadiness({
    platform,
    caption: body ?? '',
    mediaUrls: mediaUrls ?? [],
    mediaType: mediaType ?? null,
    rowStatus: 'draft',
  }).canPublish;
}
